import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, FileWarning } from "lucide-react";
import { fmtINR } from "@/lib/format";

const fyStart = () => { const d = new Date(); return new Date(d.getMonth() < 3 ? d.getFullYear() - 1 : d.getFullYear(), 3, 1).toISOString().slice(0, 10); };
const fyEnd   = () => { const d = new Date(); return new Date(d.getMonth() < 3 ? d.getFullYear() : d.getFullYear() + 1, 2, 31).toISOString().slice(0, 10); };

/** Cross-tab: Books TDS Receivable vs uploaded Form 26AS entries. */
export default function TDSReconcile26AS() {
  const [from, setFrom] = useState(fyStart());
  const [to, setTo] = useState(fyEnd());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.get("/tds/reports/receivable-vs-26as", { params: { from_date: from, to_date: to } });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const t = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="tds-reconcile-26as">
      <div className="card-sharp p-4 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm mt-1 h-8" data-testid="rec-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm mt-1 h-8" data-testid="rec-to" /></div>
        <Button onClick={run} disabled={busy} className="rounded-sm bg-accent text-white gap-1.5 ml-auto" data-testid="rec-run">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4" /> Reconcile</>}
        </Button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-4 gap-3">
            <KPI label="Book TDS Receivable" value={t.book_amount} sub={`${t.book_lines || 0} lines`} tone="info" />
            <KPI label="Form 26AS entries" value={t["26as_amount"]} sub={`${t["26as_entries"] || 0} rows`} tone="muted" />
            <KPI label="Matched" value={t.matched_amount} sub={`${t.matched || 0} pairs`} tone="success" icon={CheckCircle2} />
            <KPI label="Discrepancy" value={(t.book_amount || 0) - (t.matched_amount || 0)} sub={`${(t.only_in_books || 0) + (t.only_in_26as || 0)} unmatched`} tone="danger" icon={AlertTriangle} />
          </div>

          <Section title="⚠ Only in your books (chase deductor — 26AS is missing this credit)"
                   tone="warning" empty="Nothing pending — every book entry is reflected in 26AS.">
            {(data.only_in_books || []).map((b, i) => (
              <tr key={i} data-testid={`only-books-${i}`}>
                <td className="text-xs font-mono">{b.date}</td>
                <td className="text-xs">{b.section}</td>
                <td className="text-right tabular-nums font-medium text-amber-700">{fmtINR(b.amount)}</td>
                <td className="text-xs max-w-[400px] truncate" title={b.narration}>{b.narration || "—"}</td>
              </tr>
            ))}
          </Section>

          <Section title="⚡ Only in 26AS (IT dept has it, your books don't — post a JV to claim credit)"
                   tone="danger" empty="Perfectly matched — no dangling 26AS entries.">
            {(data.only_in_26as || []).map((e, i) => (
              <tr key={i} data-testid={`only-26as-${i}`}>
                <td className="text-xs font-mono">{e.date}</td>
                <td className="text-xs">{e.section}</td>
                <td className="text-right tabular-nums font-medium text-red-700">{fmtINR(e.tds_deducted)}</td>
                <td className="text-xs">{e.deductor_name} ({e.deductor_tan})</td>
              </tr>
            ))}
          </Section>
          {(data.only_in_26as || []).length > 0 && (
            <div className="flex justify-end -mt-2">
              <Button onClick={async () => {
                if (!window.confirm(`Post ${data.only_in_26as.length} auto-JVs · Dr TDS Receivable / Cr 26AS Suspense?`)) return;
                try {
                  const r = await api.post("/tds/reports/26as-auto-jv", {});
                  toast.success(`✓ Posted ${r.data.posted} auto-JV(s) · reclassify from TDS 26AS Adjustment when ready`);
                  run();
                } catch (e) { toast.error(e?.response?.data?.detail || "Auto-post failed"); }
              }} className="rounded-sm bg-red-600 hover:bg-red-700 text-white gap-1.5" data-testid="auto-post-26as">
                <FileWarning className="w-4 h-4" /> Auto-post {data.only_in_26as.length} missing credit(s)
              </Button>
            </div>
          )}

          <Section title="✓ Matched entries" tone="success" empty="No matches yet — upload 26AS then re-run.">
            {(data.matched || []).map((b, i) => (
              <tr key={i} data-testid={`matched-${i}`}>
                <td className="text-xs font-mono">{b.date}</td>
                <td className="text-xs">{b.section}</td>
                <td className="text-right tabular-nums text-emerald-700">{fmtINR(b.amount)}</td>
                <td className="text-xs text-emerald-600">✓ 26AS matched</td>
              </tr>
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, sub, tone = "muted", icon: Icon }) {
  const toneMap = { muted: "text-slate-700", info: "text-blue-600", success: "text-emerald-600", warning: "text-amber-600", danger: "text-red-600" };
  return (
    <div className="card-sharp p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`text-2xl font-medium mt-1 tabular-nums ${toneMap[tone]}`}>{fmtINR(value || 0)}</div>
      {sub && <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, tone, children, empty }) {
  const border = { warning: "border-amber-300", danger: "border-red-300", success: "border-emerald-300" }[tone] || "";
  const rows = React.Children.count(children);
  return (
    <div className={`card-sharp overflow-hidden ${border ? "border-l-4 " + border : ""}`}>
      <div className="px-4 py-3 border-b text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{title}</div>
      <table className="w-full dense">
        <thead><tr><th>Date</th><th>Section</th><th className="text-right">Amount</th><th>Detail</th></tr></thead>
        <tbody>
          {rows === 0 && <tr><td colSpan={4} className="text-center py-6 text-muted-foreground text-xs">{empty}</td></tr>}
          {children}
        </tbody>
      </table>
    </div>
  );
}
